import type { ReactNode } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { SettingsNumberField } from '../../../../components/settings/primitives';
import { decimalToWholePercent, wholePercentToDecimal } from './shared';

interface AmountMismatchThresholdFieldsProps {
    baseDollar: number;
    /** Stored as a 0..1 decimal. */
    percent: number;
    dollar: number;
    onBaseDollarChange: (next: number) => void;
    onPercentChange: (next: number) => void;
    onDollarChange: (next: number) => void;
    isDisabled?: boolean;
    idPrefix: string;
    trailing?: ReactNode;
}

function LabeledField({ label, children }: { label: string; children: ReactNode }) {
    return (
        <VStack spacing={1} align="flex-start">
            <Text fontSize="xs" color="gray.500" fontWeight="medium" lineHeight="short">
                {label}
            </Text>
            {children}
        </VStack>
    );
}

/** Switchover, percent, and absolute difference for an amount-mismatch rule. */
export function AmountMismatchThresholdFields({
    baseDollar,
    percent,
    dollar,
    onBaseDollarChange,
    onPercentChange,
    onDollarChange,
    isDisabled = false,
    idPrefix,
    trailing,
}: AmountMismatchThresholdFieldsProps) {
    return (
        <HStack spacing={3} align="flex-end" flexWrap="wrap">
            <LabeledField label="If under">
                <SettingsNumberField
                    value={baseDollar}
                    onChange={onBaseDollarChange}
                    allowEmpty={false}
                    min={0}
                    width="110px"
                    prefix="$"
                    ariaLabel={`${idPrefix} line total threshold`}
                    isDisabled={isDisabled}
                />
            </LabeledField>
            <LabeledField label="Flag at">
                <SettingsNumberField
                    value={decimalToWholePercent(percent)}
                    onChange={(next) => onPercentChange(wholePercentToDecimal(next))}
                    allowEmpty={false}
                    min={0}
                    max={100}
                    width="90px"
                    unit="%"
                    ariaLabel={`${idPrefix} percent threshold`}
                    isDisabled={isDisabled}
                />
            </LabeledField>
            <LabeledField label="Otherwise">
                <SettingsNumberField
                    value={dollar}
                    onChange={onDollarChange}
                    allowEmpty={false}
                    min={0}
                    width="110px"
                    prefix="$"
                    ariaLabel={`${idPrefix} difference threshold`}
                    isDisabled={isDisabled}
                />
            </LabeledField>
            {trailing && (
                <Box pb="6px" alignSelf="flex-end">
                    {trailing}
                </Box>
            )}
        </HStack>
    );
}
